"use client";

import { useEffect, useState } from "react";

/**
 * The current instant, re-read on a schedule — the one moving part behind the
 * world-clock card.
 *
 * One timer for the whole row, not one per clock: every clock reads the same
 * instant and differs only in the zone it renders it in, so separate timers
 * would cost four times as much and could still land on different seconds,
 * showing two cities a second apart.
 *
 * Two details that a plain setInterval gets wrong:
 *
 *  - It is a chain of setTimeouts aligned to the next boundary, so a clock
 *    changes ON the second rather than drifting a little further past it with
 *    every tick. setInterval accumulates the browser's scheduling delay; over a
 *    long-lived dashboard tab that becomes visibly late.
 *  - It stops while the tab is hidden and re-reads the clock the moment it comes
 *    back. Background timers are throttled to something like once a minute
 *    anyway, so the alternative is a card that wakes up showing a stale time
 *    until its next tick.
 */
export function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };

    const schedule = () => {
      // Time to the next boundary, so ticks land on it however late this one was.
      const delay = intervalMs - (Date.now() % intervalMs);

      timer = setTimeout(() => {
        setNow(new Date());
        schedule();
      }, delay);
    };

    const onVisibility = () => {
      stop();

      if (document.visibilityState === "visible") {
        // Catch up first: the tab may have been hidden for hours.
        setNow(new Date());
        schedule();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);

  return now;
}
